import { useEffect, useRef, useState } from 'react';

const DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
const SLOT_COLORS = ['#f0f0f0', '#fff3cd', '#ffe69c', '#ffc107', '#ffb300', '#28a745', '#28a745', '#198754', '#198754', '#0f5132', '#0f5132'];
const slotColor = (v: number) => SLOT_COLORS[Math.min(v, 10)] || '#f0f0f0';
const slotText = (v: number) => (v >= 3 ? '#fff' : '#555');

export type Slots = number[][]; // 7 x 24

export function emptySlots(): Slots {
  return Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => 0));
}
export function normalizeSlots(input: unknown): Slots {
  try {
    const arr = typeof input === 'string' ? JSON.parse(input) : input;
    if (Array.isArray(arr) && arr.length === 7 && Array.isArray(arr[0])) {
      return arr.map((row: number[]) => Array.from({ length: 24 }, (_, h) => Number(row[h]) || 0));
    }
  } catch { /* noop */ }
  return emptySlots();
}

const PRESETS: Record<string, (d: number, h: number) => number> = {
  business: (d, h) => (d < 5 && h >= 8 && h < 18 ? 10 : 0),
  even: () => 5,
  default: (d, h) => {
    const hourly = [1, 1, 1, 1, 1, 2, 3, 5, 7, 8, 9, 10, 10, 9, 8, 8, 9, 10, 9, 7, 5, 4, 3, 2];
    return d >= 5 ? Math.max(0, hourly[h] - 2) : hourly[h];
  },
  clear: () => 0,
};

export function PacingGrid({ value, onChange }: { value: Slots; onChange: (s: Slots) => void }) {
  const [paint, setPaint] = useState(5);
  const painting = useRef(false);

  useEffect(() => {
    const up = () => { painting.current = false; };
    window.addEventListener('mouseup', up);
    return () => window.removeEventListener('mouseup', up);
  }, []);

  const apply = (d: number, h: number, v: number) => {
    const next = value.map((row) => row.slice());
    next[d][h] = v;
    onChange(next);
  };
  const preset = (name: string) => {
    const fn = PRESETS[name];
    onChange(Array.from({ length: 7 }, (_, d) => Array.from({ length: 24 }, (_, h) => fn(d, h))));
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <label className="text-xs text-slate-600 flex items-center gap-2">
          Paint:
          <input type="range" min={0} max={10} value={paint} onChange={(e) => setPaint(Number(e.target.value))} className="w-40" />
          <span className="inline-block w-6 text-center text-white rounded px-1 text-xs font-bold" style={{ background: slotColor(paint) }}>{paint}</span>
        </label>
        <div className="flex gap-1.5">
          <button type="button" onClick={() => preset('business')} className="px-2 py-1 rounded text-xs border border-border hover:bg-slate-50">Business Hours</button>
          <button type="button" onClick={() => preset('even')} className="px-2 py-1 rounded text-xs border border-border hover:bg-slate-50">24/7 Even</button>
          <button type="button" onClick={() => preset('default')} className="px-2 py-1 rounded text-xs border border-border hover:bg-slate-50">Default Pattern</button>
          <button type="button" onClick={() => preset('clear')} className="px-2 py-1 rounded text-xs bg-rose-500 text-white hover:bg-rose-600">Clear All</button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="border-separate" style={{ borderSpacing: 2, userSelect: 'none' }}>
          <thead>
            <tr>
              <th className="w-12" />
              {Array.from({ length: 24 }, (_, h) => (
                <th key={h} className="text-[10px] font-normal text-slate-400 w-7 text-center">{String(h).padStart(2, '0')}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {DAYS.map((day, d) => (
              <tr key={day}>
                <td className="text-[11px] font-bold text-slate-600 pr-1.5 whitespace-nowrap">{day}</td>
                {Array.from({ length: 24 }, (_, h) => {
                  const v = value[d]?.[h] ?? 0;
                  return (
                    <td
                      key={h}
                      onMouseDown={() => { painting.current = true; apply(d, h, paint); }}
                      onMouseEnter={() => { if (painting.current) apply(d, h, paint); }}
                      title={`${day} ${String(h).padStart(2, '0')}h: ${v}`}
                      className="w-7 h-7 text-center align-middle cursor-pointer text-[11px] font-bold rounded"
                      style={{ background: slotColor(v), color: slotText(v) }}
                    >
                      {v || ''}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-400 mt-2">Click and drag to paint the intensity (0–10) per hour/day.</p>
    </div>
  );
}
